/**
 * handbookPrint — Jun 2026 (founder succession-planning request).
 * Renders cheat sheets (+ worked examples) into a print-friendly window so
 * any owner can "Print / Save as PDF" a single sheet or the full handbook.
 */
import CHEAT_SHEETS from '../data/cheatSheets';
import CHEAT_SHEET_EXAMPLES from '../data/cheatSheetExamples';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const sheetHTML = (pageId) => {
  const sheet = CHEAT_SHEETS[pageId];
  if (!sheet) return '';
  const examples = CHEAT_SHEET_EXAMPLES[pageId] || [];
  return `
  <section class="sheet">
    <h2>${esc(sheet.title)}</h2>
    ${sheet.sections.map((sec) => `
      <h3>${esc(sec.heading)}</h3>
      <ul>${sec.bullets.map((b) => `<li>${esc(b)}</li>`).join('')}</ul>
    `).join('')}
    ${examples.length ? `
      <h3 class="ex-title">Worked Examples</h3>
      ${examples.map((ex) => `
        <div class="example">
          <p class="ex-label">${esc(ex.label)}</p>
          <p class="ex-scenario">${esc(ex.scenario)}</p>
          <ol>${ex.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
        </div>
      `).join('')}
    ` : ''}
  </section>`;
};

const shellHTML = (title, body) => `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1A1209; max-width: 800px; margin: 0 auto; padding: 32px 24px; }
  .cover h1 { font-size: 26px; margin: 0 0 4px; }
  .cover p { color: #5B5348; font-size: 13px; margin: 0 0 28px; }
  .sheet { page-break-after: always; }
  .sheet:last-child { page-break-after: auto; }
  h2 { font-size: 19px; color: #F5831F; border-bottom: 2px solid #F5831F; padding-bottom: 6px; margin: 28px 0 12px; }
  h3 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; color: #7B4FD8; margin: 18px 0 6px; }
  h3.ex-title { color: #2EAA5C; }
  ul, ol { margin: 4px 0 12px; padding-left: 22px; }
  li { font-size: 13px; line-height: 1.55; margin-bottom: 4px; }
  .example { background: #FAFAF8; border: 1px solid #E8E0D8; border-radius: 8px; padding: 12px 16px; margin-bottom: 12px; }
  .ex-label { font-weight: 700; font-size: 13px; margin: 0 0 2px; }
  .ex-scenario { font-style: italic; color: #5B5348; font-size: 12.5px; margin: 0 0 6px; }
  @media print { body { padding: 0; } }
</style></head>
<body>
  <div class="cover">
    <h1>${esc(title)}</h1>
    <p>HiBFF Command Centre · generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · use your browser's "Save as PDF" in the print dialog</p>
  </div>
  ${body}
  <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
</body></html>`;

const openPrintWindow = (title, body) => {
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return;
  w.document.write(shellHTML(title, body));
  w.document.close();
};

export const printSheet = (pageId) => {
  const sheet = CHEAT_SHEETS[pageId];
  if (!sheet) return;
  openPrintWindow(`HiBFF Cheat Sheet — ${sheet.title}`, sheetHTML(pageId));
};

export const printHandbook = () => {
  const body = Object.keys(CHEAT_SHEETS).map(sheetHTML).join('');
  openPrintWindow("HiBFF Owner's Handbook — Complete Command Centre Guide", body);
};
